import { StatusFile } from "@/common/enums/enums";
import { queryClient } from "@/lib/query-client";
import { getVisitScope, visitListOptions } from "@/lib/visit-queries";
import { sortVisitSummaries } from "@/services/visits/read-model";

// The visit "Try it" opens: the newest visit with a finished note, else the
// newest visit. Uses the cached visit list when there is one.
export const findLatestVisitId = async (): Promise<string | null> => {
  const scope = getVisitScope();
  if (!scope) return null;
  const options = visitListOptions(scope);
  const data =
    queryClient.getQueryData(options.queryKey) ??
    (await queryClient.fetchInfiniteQuery(options));
  const visits = sortVisitSummaries(data.pages.flatMap((page) => page.items));
  const ready = visits.find((visit) => visit.status === StatusFile.READY);
  return (ready ?? visits[0])?.public_id ?? null;
};

// Brings a control into view and outlines it for a moment, so the provider
// sees where the feature lives. Waits for it to appear (a panel that is
// still opening), up to a few seconds.
const SPOTLIGHT_CLASSES = [
  "ring-2",
  "ring-primary",
  "ring-offset-2",
  "ring-offset-background",
  "transition-shadow",
];

export const spotlight = (
  selector: string,
  {
    timeoutMs = 4000,
    holdMs = 2400,
    focus = true,
  }: { timeoutMs?: number; holdMs?: number; focus?: boolean } = {},
): (() => void) => {
  let cancelled = false;
  let frame = 0;
  let release: ReturnType<typeof setTimeout> | undefined;
  let lit: HTMLElement | null = null;
  const unlight = (): void => {
    if (!lit) return;
    lit.classList.remove(...SPOTLIGHT_CLASSES);
    lit.removeAttribute("data-whats-new-spotlight");
    lit = null;
  };
  const started = Date.now();
  const tick = (): void => {
    if (cancelled) return;
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) {
      if (Date.now() - started < timeoutMs) frame = requestAnimationFrame(tick);
      return;
    }
    element.scrollIntoView?.({ block: "center", behavior: "smooth" });
    if (focus) element.focus?.({ preventScroll: true });
    element.classList.add(...SPOTLIGHT_CLASSES);
    element.setAttribute("data-whats-new-spotlight", "true");
    lit = element;
    release = setTimeout(unlight, holdMs);
  };
  frame = requestAnimationFrame(tick);
  return (): void => {
    cancelled = true;
    cancelAnimationFrame(frame);
    if (release) clearTimeout(release);
    unlight();
  };
};
